// Phase RV-4a — Window config display helpers.
//
// Source: ROLLOVER_Intelligence_Enhancement_v1.0 §04 (Window Architecture).
//
// Pure, no I/O. Reused by the chain rail chip and the WindowConfigStep
// preview. Day numbering: 0=Sun, 6=Sat (matches DB column custom_days).

export type WindowMode =
  | "midweek_only"
  | "weekend_only"
  | "both_windows"
  | "custom";

export const WINDOW_MODES: readonly WindowMode[] = [
  "midweek_only",
  "weekend_only",
  "both_windows",
  "custom",
] as const;

export interface WindowConfig {
  mode: WindowMode;
  custom_days?: number[];
}

export interface WindowModeMeta {
  mode: WindowMode;
  title: string;
  bestFor: string;
  cadence: string;
}

// §04 Table 12.
export const WINDOW_MODE_META: Record<WindowMode, WindowModeMeta> = {
  midweek_only: {
    mode: "midweek_only",
    title: "Midweek Only",
    bestFor: "Best for cup nights + Tue/Wed UCL weeks",
    cadence: "1 cycle / week",
  },
  weekend_only: {
    mode: "weekend_only",
    title: "Weekend Only",
    bestFor: "Best for league-heavy slates and supplements",
    cadence: "1 cycle / week",
  },
  both_windows: {
    mode: "both_windows",
    title: "Both Windows",
    bestFor: "Best when both windows show qualifying combos",
    cadence: "Up to 2 cycles / week",
  },
  custom: {
    mode: "custom",
    title: "Custom",
    bestFor: "Best for users with specific match-day preferences",
    cadence: "Picks the day(s) you choose",
  },
};

// 0=Sun..6=Sat — short labels used in the chip and the day toggle.
export const DAY_SHORT: readonly string[] = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
] as const;

/** Validate a day index (0..6). */
export function isValidDay(n: number): boolean {
  return Number.isInteger(n) && n >= 0 && n <= 6;
}

/** Returns true if the config is valid (custom mode requires 1..7 days). */
export function isValidWindowConfig(cfg: WindowConfig): boolean {
  if (!WINDOW_MODES.includes(cfg.mode)) return false;
  if (cfg.mode !== "custom") return true;
  const days = cfg.custom_days ?? [];
  if (days.length < 1 || days.length > 7) return false;
  const seen = new Set<number>();
  for (const d of days) {
    if (!isValidDay(d)) return false;
    if (seen.has(d)) return false;
    seen.add(d);
  }
  return true;
}

/**
 * Render the window config as a short uppercase chip label.
 *   midweek_only  → "MIDWEEK ONLY"
 *   weekend_only  → "WEEKEND ONLY"
 *   both_windows  → "BOTH WINDOWS"
 *   custom        → "Mon, Tue, Sat" (proper-case, comma-joined)
 *
 * Custom with no/invalid days falls back to "CUSTOM" so the UI never
 * renders an empty chip.
 */
export function formatWindowConfig(
  mode: WindowMode,
  custom_days?: number[] | null,
): string {
  if (mode === "midweek_only") return "MIDWEEK ONLY";
  if (mode === "weekend_only") return "WEEKEND ONLY";
  if (mode === "both_windows") return "BOTH WINDOWS";
  // custom
  const days = (custom_days ?? []).filter(isValidDay);
  if (days.length === 0) return "CUSTOM";
  // De-dup + sort ascending so "Mon, Tue, Sat" is stable regardless of
  // the order the user toggled the days in.
  const unique = Array.from(new Set(days)).sort((a, b) => a - b);
  return unique.map((d) => DAY_SHORT[d]).join(", ");
}
